import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'
import useModuleAccess from '../hooks/useModuleAccess'

const API_URL = process.env.REACT_APP_API_URL

function parseApiResponse(raw) {
  let d = raw
  if (typeof d === 'string') {
    try {
      d = JSON.parse(d)
    } catch {
      return raw
    }
  }
  if (d && d.body != null) {
    if (typeof d.body === 'string') {
      try {
        return JSON.parse(d.body)
      } catch {
        return d.body
      }
    }
    return d.body
  }
  return d
}

function parseJsonIfString(value) {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

function normalizeApiPayload(raw) {
  let obj = raw
  if (!obj || typeof obj !== 'object') return raw
  obj = { ...obj }
  if (typeof obj.data === 'string') {
    obj.data = parseJsonIfString(obj.data)
  }
  return obj
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== 'object') return false
  const s = responseData.success
  return s === true || s === 'true' || s === 1 || s === '1'
}

function pickCustomerFromResponse(responseData, id) {
  let dataBlock = responseData?.data != null ? responseData.data : responseData
  if (dataBlock == null) return null
  if (typeof dataBlock === 'string') {
    dataBlock = parseJsonIfString(dataBlock)
  }
  if (!dataBlock || typeof dataBlock !== 'object') return null

  if (dataBlock.customer && typeof dataBlock.customer === 'object') {
    return dataBlock.customer
  }

  /** Single customer document (not wrapped in customers[]) — same shape as list API */
  if (
    !Array.isArray(dataBlock) &&
    dataBlock.customers == null &&
    (dataBlock._id != null || dataBlock.customerName != null)
  ) {
    return dataBlock
  }

  const list = dataBlock.customers
  if (!Array.isArray(list) || list.length === 0) return null
  const found = list.find((c) => String(c._id || c.id) === String(id))
  return found || list[0]
}

function displayUser(ref) {
  if (!ref) return '-'
  if (typeof ref === 'string') return ref
  if (ref.name) return ref.name
  if (ref.email) return ref.email
  return '-'
}

const CustomerMasterView = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { canUpdate } = useModuleAccess('customer_master')
  const [customer, setCustomer] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  const formatDateToIST = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    })
  }

  const fetchCustomer = async () => {
    if (!id) {
      toast.error('Customer ID is missing', { autoClose: 2000 })
      navigate('/customerMaster')
      return
    }

    setIsLoading(true)
    try {
      const payload = {
        action: 'get-customer-by-id',
        id
      }

      const response = await axios.post(API_URL, payload)
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        const row = pickCustomerFromResponse(responseData, id)
        if (row) {
          setCustomer(row)
        } else {
          toast.error('Customer not found', { autoClose: 2000 })
          navigate('/customerMaster')
        }
      } else {
        toast.error('Failed to fetch customer details', { autoClose: 2000 })
        navigate('/customerMaster')
      }
    } catch (error) {
      console.error('Fetch customer error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      const msg =
        typeof errData === 'object' && errData?.message
          ? errData.message
          : 'An error occurred while fetching customer details'
      toast.error(msg, { autoClose: 2000 })
      navigate('/customerMaster')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchCustomer()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const cid = customer?._id || customer?.id || id

  if (isLoading) {
    return (
      <div>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-6 col-sm-6">
                <h1 className="m-0 txt_lft">View Customer</h1>
              </div>
              <div className="col-6 col-sm-6">
                <ol className="breadcrumb justify-content-end">
                  <li className="breadcrumb-item">
                    <Link to="/customerMaster">
                      <i className="fa-solid fa-arrow-left-long" /> Back
                    </Link>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!customer) {
    return (
      <div>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-6 col-sm-6">
                <h1 className="m-0 txt_lft">View Customer</h1>
              </div>
              <div className="col-6 col-sm-6">
                <ol className="breadcrumb justify-content-end">
                  <li className="breadcrumb-item">
                    <Link to="/customerMaster">
                      <i className="fa-solid fa-arrow-left-long" /> Back
                    </Link>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <p>Customer not found</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const contacts = Array.isArray(customer.contacts) ? customer.contacts : []

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">View Customer</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/customerMaster">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <form className="form-horizontal">
                    <div className="card-body">
                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">Customer Name</label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{customer.customerName || '-'}</div>
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">GST Number</label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{customer.gstNumber || '-'}</div>
                        </div>
                      </div>

                        {/* <div className="form-group row align-items-center">
                          <label className="col-12 col-md-4 col-form-label">Status</label>
                          <div className="col-12 col-md-8">
                            <div className="txtdsply">
                              <span
                                className={`badge ${
                                  customer.status === 'active' ? 'badge-success' : 'badge-danger'
                                }`}
                                style={{ fontSize: '90%', fontWeight: 550 }}
                              >
                                {customer.status || '-'}
                              </span>
                            </div>
                          </div>
                        </div> */}

                      <div className="row mb-2 mt-3">
                        <div className="col-12">
                          <h5 className="text-primary border-bottom pb-2 mb-3">
                            <i className="fa-solid fa-address-book mr-2" />
                            Contacts
                          </h5>
                        </div>
                      </div>

                      {contacts.length === 0 ? (
                        <div className="form-group row">
                          <div className="col-12">
                            <div className="txtdsply text-muted">No contacts</div>
                          </div>
                        </div>
                      ) : (
                        contacts.map((contact, idx) => (
                          <div
                            key={contact._id || idx}
                            className="border rounded p-3 mb-3"
                            style={{ backgroundColor: '#f8f9fa' }}
                          >
                            <h6 className="text-primary mb-3 fwt_600">Contact {idx + 1}</h6>

                            <div className="form-group row align-items-center">
                              <label className="col-12 col-md-4 col-form-label">Contact Person</label>
                              <div className="col-12 col-md-8">
                                <div className="txtdsply">{contact.contactPerson || '-'}</div>
                              </div>
                            </div>

                            <div className="form-group row align-items-center">
                              <label className="col-12 col-md-4 col-form-label">Designation</label>
                              <div className="col-12 col-md-8">
                                <div className="txtdsply">{contact.designation || '-'}</div>
                              </div>
                            </div>

                            <div className="form-group row align-items-center">
                              <label className="col-12 col-md-4 col-form-label">Email</label>
                              <div className="col-12 col-md-8">
                                <div className="txtdsply">{contact.email || '-'}</div>
                              </div>
                            </div>

                            <div className="form-group row align-items-center">
                              <label className="col-12 col-md-4 col-form-label">Contact Number</label>
                              <div className="col-12 col-md-8">
                                <div className="txtdsply">{contact.contactNumber || '-'}</div>
                              </div>
                            </div>

                            <div className="form-group row align-items-center">
                              <label className="col-12 col-md-4 col-form-label">Address</label>
                              <div className="col-12 col-md-8">
                                <div className="txtdsply">{contact.address || '-'}</div>
                              </div>
                            </div>

                            <div className="form-group row align-items-center">
                              <label className="col-12 col-md-4 col-form-label">City</label>
                              <div className="col-12 col-md-8">
                                <div className="txtdsply">{contact.city || '-'}</div>
                              </div>
                            </div>

                            <div className="form-group row align-items-center">
                              <label className="col-12 col-md-4 col-form-label">State</label>
                              <div className="col-12 col-md-8">
                                <div className="txtdsply">{contact.state || '-'}</div>
                              </div>
                            </div>

                            <div className="form-group row align-items-center mb-0">
                              <label className="col-12 col-md-4 col-form-label">Status</label>
                              <div className="col-12 col-md-8">
                                <div className="txtdsply">
                                  <span
                                    className={`badge ${
                                      contact.status === 'active' ? 'badge-success' : 'badge-secondary'
                                    }`}
                                    style={{ fontSize: '90%', fontWeight: 550 }}
                                  >
                                    {contact.status || '-'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      )}

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">Created By</label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{displayUser(customer.createdBy)}</div>
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">Created At</label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{formatDateToIST(customer.createdAt)}</div>
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">Updated By</label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{displayUser(customer.updatedBy)}</div>
                        </div>
                      </div>

                      <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">Updated At</label>
                        <div className="col-12 col-md-8">
                          <div className="txtdsply">{formatDateToIST(customer.updatedAt)}</div>
                        </div>
                      </div>
                    </div>

                    {canUpdate && (
                      <div className="card-footer text-center">
                        <Link to={`/customerMasterEdit/${cid}`} className="btn btn-blue mr-2">
                          Edit
                        </Link>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CustomerMasterView
